// src/services/apaleo/groups.ts
// Extracted verbatim from src/services/apaleo.ts (Phase 5 of
// plans/monolith-migration-plan.md). `this` is typed via ThisType<ApaleoClient>.

import type { AmountModel, FolioModel, BlockModel, GroupModel, RoutingModel, GroupCreatePayload } from './types';
import type { ApaleoClient } from './client-contract';
import { IS_DEV, MockData, ALLOWED_PROPERTIES } from './config';

const part: ThisType<ApaleoClient> = {
async fetchGroups(
  propertyId: string | null = null,
  textSearch = "",
  dateFrom: string | null = null,
  dateTo: string | null = null,
): Promise<GroupModel[]> {
  if (IS_DEV) {
    const expandedGroups = MockData.MOCK_GROUPS.map(g => ({
      ...g,
      blocks: MockData.MOCK_BLOCKS.filter(b => b.groupId === g.id)
    }));
    let filtered = expandedGroups;
    if (propertyId && propertyId !== "All") {
      filtered = filtered.filter(g => (g.propertyIds || []).includes(propertyId));
    }
    if (textSearch) {
      const query = textSearch.toLowerCase();
      filtered = filtered.filter(g =>
        (g.name && g.name.toLowerCase().includes(query)) ||
        (g.id && g.id.toLowerCase().includes(query)) ||
        (g.bookingId && g.bookingId.toLowerCase().includes(query)) ||
        (g.booker && (
          (g.booker.lastName && g.booker.lastName.toLowerCase().includes(query)) ||
          (g.booker.firstName && g.booker.firstName.toLowerCase().includes(query))
        ))
      );
    }
    if (dateFrom || dateTo) {
      filtered = filtered.filter(g => {
        return g.blocks?.some(block => {
          const blockFromDate = new Date((block.from || "").split("T")[0]);
          const blockToDate = new Date((block.to || "").split("T")[0]);
          const filterFromDate = dateFrom ? new Date(dateFrom) : null;
          const filterToDate = dateTo ? new Date(dateTo) : null;

          if (filterFromDate && blockToDate < filterFromDate) return false;
          if (filterToDate && blockFromDate > filterToDate) return false;
          return true;
        });
      });
    }
    return filtered;
  }
  try {
    let queryParams = "expand=blocks&pageSize=100";
    if (propertyId && propertyId !== "All") {
      queryParams += `&propertyIds=${propertyId}`;
    } else if (ALLOWED_PROPERTIES && ALLOWED_PROPERTIES.length > 0) {
      queryParams += `&propertyIds=${ALLOWED_PROPERTIES.join(",")}`;
    }
    if (textSearch) {
      queryParams += `&textSearch=${encodeURIComponent(textSearch)}`;
    }
    if (dateFrom) {
      const fromIso = dateFrom.includes("T") ? dateFrom : `${dateFrom}T00:00:00Z`;
      queryParams += `&from=${fromIso}`;
    }
    if (dateTo) {
      const toIso = dateTo.includes("T") ? dateTo : `${dateTo}T23:59:59Z`;
      queryParams += `&to=${toIso}`;
    }

    // Parallel search fallbacks for Booking IDs and exact Group IDs (only query if search term matches exact ID patterns)
    let bookingGroupPromise: Promise<GroupModel | null> = Promise.resolve(null);
    let groupByIdPromise: Promise<GroupModel | null> = Promise.resolve(null);

    if (textSearch && textSearch.trim()) {
      const cleanSearch = textSearch.trim();
      const isExactId = /^[a-zA-Z0-9]{8}$/.test(cleanSearch) || /^(GRP-|B-)[0-9]+$/i.test(cleanSearch);

      if (isExactId) {
        bookingGroupPromise = this._fetch(`/booking/v1/bookings/${cleanSearch}`)
          .then(async (booking) => {
            const groupId = (booking as { groupId?: string } | null)?.groupId;
            if (groupId) {
              return (await this._fetch(`/booking/v1/groups/${groupId}?expand=blocks`)) as GroupModel;
            }
            return null;
          })
          .catch(() => null);

        groupByIdPromise = this._fetch(`/booking/v1/groups/${cleanSearch}?expand=blocks`)
          .then((g) => g as GroupModel)
          .catch(() => null);
      }
    }

    const [mainResult, bookingGroupResult, groupByIdResult] = await Promise.all([
      this._fetch(`/booking/v1/groups?${queryParams}`)
        .then((r) => r as { groups?: GroupModel[] })
        .catch(() => ({ groups: [] as GroupModel[] })),
      bookingGroupPromise,
      groupByIdPromise
    ]);

    const groups: GroupModel[] = mainResult?.groups || [];
    if (groupByIdResult && !groups.some(g => g.id === groupByIdResult.id)) {
      groups.push(groupByIdResult);
    }
    if (bookingGroupResult && !groups.some(g => g.id === bookingGroupResult.id)) {
      groups.push(bookingGroupResult);
    }

    // Normalize blocks inside groups for UI consistency
    return groups.map(group => {
      if (group.blocks) {
        group.blocks = group.blocks.map(block => {
          const blockedUnits = block.blockedUnits !== undefined ? block.blockedUnits : 0;
          const pickedUpUnits = block.pickedReservations !== undefined ? block.pickedReservations : (block.pickedUpUnits || 0);
          return {
            ...block,
            blockedUnits,
            pickedUpUnits
          };
        });
      }
      return group;
    });
  } catch (error) {
    console.error("Fehler beim Laden der Gruppen:", error);
    return [];
  }
},

async fetchBlocks(groupId: string): Promise<BlockModel[]> {
  if (IS_DEV) {
    return MockData.MOCK_BLOCKS.filter(b => b.groupId === groupId);
  }
  try {
    const data = (await this._fetch(`/booking/v1/blocks?groupId=${groupId}&expand=timeSlices&pageSize=100`)) as
      | { blocks?: BlockModel[] }
      | null;
    const blocks = data?.blocks || [];
    return blocks.map(block => {
      const timeSlices = block.timeSlices || [];
      const blockedUnits = timeSlices.length > 0 ? Math.max(...timeSlices.map(ts => ts.blockedUnits || 0)) : 0;
      const pickedUpUnits = block.pickedReservations !== undefined ? block.pickedReservations : (timeSlices.length > 0 ? Math.max(...timeSlices.map(ts => ts.pickedUnits || 0)) : 0);
      return {
        ...block,
        blockedUnits,
        pickedUpUnits
      };
    });
  } catch (error) {
    console.error(`Fehler beim Laden der Blocks für Gruppe ${groupId}:`, error);
    return [];
  }
},

async createGroup(payload: GroupCreatePayload): Promise<unknown> {
  if (IS_DEV) {
    const newGroup = {
      id: `GRP-${Math.floor(100 + Math.random() * 900)}`,
      name: payload.name,
      booker: payload.booker,
      propertyIds: payload.propertyIds,
      comment: payload.comment || "",
      bookingId: `B-${Math.floor(100 + Math.random() * 900)}`
    };
    MockData.MOCK_GROUPS.push(newGroup);
    return newGroup;
  }
  try {
    return await this._fetch("/booking/v1/groups", {
      method: "POST",
      body: JSON.stringify(payload),
    });
  } catch (error) {
    console.error("Fehler beim Erstellen der Gruppe:", error);
    throw error;
  }
},

async updateBlock(
  blockId: string,
  payload: { from?: string; to?: string; grossDailyRate?: AmountModel; blockedUnits?: number },
): Promise<unknown> {
  if (IS_DEV) {
    const mock = MockData.MOCK_BLOCKS.find(b => b.id === blockId);
    if (mock) {
      if (payload.from !== undefined) mock.from = payload.from;
      if (payload.to !== undefined) mock.to = payload.to;
      if (payload.grossDailyRate) mock.grossDailyRate = payload.grossDailyRate;
      if (payload.blockedUnits !== undefined) mock.blockedUnits = payload.blockedUnits;
    }
    return { id: blockId };
  }
  // Per-night time slices, "to" exclusive - pure date-string math with the
  // local Date constructor (no toISOString, RULESET section 2.2 gotcha).
  const fromDate = (payload.from || "").split("T")[0];
  const toDate = (payload.to || "").split("T")[0];
  const dateParts = (s: string) => s.split("-").map(Number);
  const [fy, fm, fd] = dateParts(fromDate);
  const [ty, tm, td] = dateParts(toDate);
  const nights = fy && fm && fd && ty && tm && td
    ? Math.max(0, Math.round(
        (new Date(ty, tm - 1, td).getTime() - new Date(fy, fm - 1, fd).getTime()) / 86400000,
      ))
    : 0;
  const amendBody = {
    from: payload.from,
    to: payload.to,
    grossDailyRate: payload.grossDailyRate,
    timeSlices: Array.from({ length: nights }, () => ({ blockedUnits: payload.blockedUnits || 0 })),
  };
  try {
    return await this._fetch(`/booking/v1/block-actions/${encodeURIComponent(blockId)}/amend`, {
      method: "PUT",
      body: JSON.stringify(amendBody),
    });
  } catch (error) {
    console.error(`Fehler beim Aktualisieren des Blocks ${blockId}:`, error);
    throw error;
  }
},

async createBlock(payload: GroupCreatePayload): Promise<unknown> {
  if (IS_DEV) {
    const ratePlan = MockData.MOCK_RATE_PLANS.find(rp => rp.id === payload.ratePlanId) || { name: "Standard Rate" };
    const newBlock = {
      id: `BLK-${Math.floor(100 + Math.random() * 900)}`,
      groupId: payload.groupId,
      from: payload.from,
      to: payload.to,
      ratePlan: { id: payload.ratePlanId, name: ratePlan.name },
      unitGroup: { id: payload.unitGroupId || "UG-DBL", name: "Double Room" },
      grossDailyRate: payload.grossDailyRate,
      blockedUnits: payload.blockedUnits || 1,
      pickedUpUnits: 0
    };
    MockData.MOCK_BLOCKS.push(newBlock);
    return newBlock;
  }
  try {
    return await this._fetch("/booking/v1/blocks", {
      method: "POST",
      body: JSON.stringify(payload),
    });
  } catch (error) {
    console.error("Fehler beim Erstellen des Blocks:", error);
    throw error;
  }
},

async addReservationToGroup(
  groupId: string,
  reservations: { blockId?: string }[],
): Promise<unknown> {
  if (IS_DEV) {
    const response = {
      reservationIds: reservations.map(() => `RES-${Math.floor(1000 + Math.random() * 9000)}`)
    };
    // Find associated block and increment pickedUpUnits and pickedReservations
    reservations.forEach(res => {
      const block = MockData.MOCK_BLOCKS.find(b => b.id === res.blockId);
      if (block) {
        block.pickedUpUnits = (block.pickedUpUnits || 0) + 1;
        block.pickedReservations = (block.pickedReservations || 0) + 1;
      }
    });
    return response;
  }
  try {
    return await this._fetch(`/booking/v1/groups/${groupId}/reservations`, {
      method: "POST",
      body: JSON.stringify({ reservations }),
    });
  } catch (error) {
    console.error(`Fehler beim Hinzufügen von Reservierungen zur Gruppe ${groupId}:`, error);
    throw error;
  }
},

async fetchRoutings(bookingId: string): Promise<RoutingModel[]> {
  if (IS_DEV) {
    return MockData.MOCK_ROUTINGS.filter(r => r.bookingId === bookingId);
  }
  try {
    const data = (await this._fetch(`/finance/v1/routings?bookingIds=${bookingId}`)) as
      | { routings?: RoutingModel[] }
      | null;
    return data?.routings || [];
  } catch (error) {
    console.error(`Fehler beim Laden der Routings für Buchung ${bookingId}:`, error);
    return [];
  }
},

async createRouting(payload: GroupCreatePayload): Promise<unknown> {
  if (IS_DEV) {
    const newRouting = {
      id: `ROT-${Math.floor(100 + Math.random() * 900)}`,
      bookingId: payload.bookingId,
      destinationFolioId: payload.destinationFolioId,
      routingType: payload.routingType || "ServiceType",
      serviceType: payload.serviceType || "Accommodation",
      startDate: payload.startDate || "",
      endDate: payload.endDate || ""
    };
    MockData.MOCK_ROUTINGS.push(newRouting);
    return newRouting;
  }
  try {
    let propertyId: string | undefined = payload.propertyId;
    if (!propertyId && payload.destinationFolioId) {
      try {
        const folio = (await this._fetch(`/finance/v1/folios/${payload.destinationFolioId}`)) as
          | FolioModel
          | { propertyId?: string }
          | null;
        propertyId =
          (folio as FolioModel).property?.id ||
          (folio as { propertyId?: string }).propertyId;
      } catch (e) {
        console.warn("Could not determine propertyId from folio:", e);
      }
    }
    if (!propertyId) {
      propertyId = ALLOWED_PROPERTIES[0] || "BERLIN";
    }

    const filter: Record<string, unknown> = {};
    if (payload.routingType === "ServiceType" && payload.serviceType) {
      filter.serviceTypes = [payload.serviceType];
    } else if (payload.routingType === "Service" && payload.serviceName) {
      filter.serviceIds = [payload.serviceName];
    }
    if (payload.startDate) {
      filter.from = payload.startDate;
    }
    if (payload.endDate) {
      filter.to = payload.endDate;
    }

    const apiPayload = {
      bookingId: payload.bookingId,
      propertyId: propertyId,
      destinationFolioId: payload.destinationFolioId,
      filter: filter
    };

    return await this._fetch("/finance/v1/routings", {
      method: "POST",
      body: JSON.stringify(apiPayload)
    });
  } catch (error) {
    console.error("Fehler beim Erstellen des Routings:", error);
    throw error;
  }
},

async deleteRouting(routingId: string): Promise<unknown> {
  if (IS_DEV) {
    const index = MockData.MOCK_ROUTINGS.findIndex(r => r.id === routingId);
    if (index !== -1) {
      MockData.MOCK_ROUTINGS.splice(index, 1);
    }
    return true;
  }
  try {
    return await this._fetch(`/finance/v1/routings/${routingId}`, {
      method: "DELETE"
    });
  } catch (error) {
    console.error(`Fehler beim Löschen des Routings ${routingId}:`, error);
    throw error;
  }
},

};

export default part;
